var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { max as _max, min as _min, cloneDeep, isNumber, keyBy, mapValues, range as rangeFromLodash, set } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { PlanType } from '../../enum';
import { useAPI } from '../../state';
import { formatNumberByLocale, getLanguage } from '../../util/locale';
import { useTerminologyTranslation } from '../app/terminology';
import { getLOBName, getUnassignedLOB, sortByLOBName } from '../common';
import { lineBarLayout } from '../common/chart/config';
import { useReportAPI } from '../report/selectors';
import { PLAN_COLORS } from '../wizard/constants';
import { selectPeriods, selectPlan, selectPlanType, selectWeekDate, selectWeeks } from '../wizard/selectors';
import { ALL_LOCATIONS_ID, ALL_QUEUES_ITEM, CHART_HEIGHT_CONFIG, COLOR_DEFAULT_VARIANCE, DAILY_INTERVAL, DAILY_VIEW_RANGE_CONFIG, DATE_PICKER_SLASH_FORMAT, LIMIT_TIMELINE, MAXIMUM_VALUES, METRIC_LIST, PERCENTAGES, RANGE_OPTIONS, SUMMARY_BOX_BASE, SUMMARY_BOX_HANDLED, SUMMARY_BOX_THRESHOLD } from './constants';
import { ACTUAL_ATTRITION_MODE, CHART_KEYS, DAILY_FILTER_KEYS, FREQUENCY_ENUM, INTERVAL_TYPE, METRICS_ENUM, PERIOD_DATA_FIELDS, PERIOD_KEY, RANGE_COLORS } from './enum';
import { useMetricSettingsAPI } from './settings/selectors';
export var LIST_QUEUE_API = '/api/queue/findAll/{planId}';
export var ACTUAL_REPORT_API = '/api/report/actual';
export var GET_ACTUAL_QUEUE_STATUS = '/api/queue/queueStatus';
export var EXPORT_API = '/api/report/actual/export';
export var QUEUE_LOCATIONS_API = '/api/queue/{queueId}/locations';
function translate(key) {
  return i18next.t(key, {
    defaultValue: key
  });
}
var selectQueues = function (queues) {
  return queues;
};
var selectQueueMap = createSelector(selectQueues, function (queues) {
  return keyBy(queues, 'id');
});
export var createQueueSelector = function (id) {
  if (id === void 0) {
    id = 0;
  }
  return createSelector(selectQueueMap, function (map) {
    return map[id];
  });
};
export var createQueueDropdownSelector = function (isAllQueue, t) {
  if (isAllQueue === void 0) {
    isAllQueue = true;
  }
  if (t === void 0) {
    t = function (k, opts) {
      var _a;
      return i18next.t("report:actual.".concat(k), {
        defaultValue: (_a = opts === null || opts === void 0 ? void 0 : opts.defaultValue) !== null && _a !== void 0 ? _a : k
      });
    };
  }
  return createSelector(selectQueues, function (queues) {
    var unassignedLOB = getUnassignedLOB();
    var queueList = (queues === null || queues === void 0 ? void 0 : queues.length) ? queues.map(function (_a) {
      var id = _a.id,
        name = _a.name,
        status = _a.status,
        lobName = _a.lobName,
        subLobName = _a.subLobName;
      return {
        uniqueId: id,
        name: name,
        isWat: Boolean(status.wat),
        lobName: getLOBName(lobName, subLobName, unassignedLOB)
      };
    }) : [];
    queueList.sort(function (a, b) {
      return sortByLOBName(a, b, unassignedLOB);
    });
    if (isAllQueue) {
      queueList.unshift(__assign(__assign({}, ALL_QUEUES_ITEM), {
        name: t('allQueues')
      }));
    }
    return queueList;
  });
};
var selectScenarios = function (plan) {
  return plan === null || plan === void 0 ? void 0 : plan.scenarios;
};
export var createScenariosDropdownSelector = function () {
  return createSelector(selectScenarios, function (scenarios) {
    return (scenarios === null || scenarios === void 0 ? void 0 : scenarios.length) ? scenarios.map(function (scenario) {
      return {
        uniqueId: scenario.id,
        name: scenario.isBaseline ? i18next.t('translation:baseline') : scenario.name
      };
    }) : [];
  });
};
// Will be update when have actuals API
export var useQueuesAPI = function (planId) {
  var _a = useAPI(LIST_QUEUE_API, {
      planId: planId
    }),
    data = _a[0],
    fetchData = _a[1],
    clearData = _a[2];
  var fetchDataWrapper = useCallback(function () {
    if (planId) {
      fetchData();
    }
  }, [fetchData, planId]);
  return [data, fetchDataWrapper, clearData];
};
export var useQueueById = function (planId, queueId) {
  var data = useQueuesAPI(planId)[0].data;
  var selectQueue = useMemo(function () {
    return createQueueSelector(queueId);
  }, [queueId]);
  return selectQueue(data);
};
export var useQueuesDropdown = function (planId, isAllQueue) {
  var data = useQueuesAPI(planId)[0].data;
  var t = useTerminologyTranslation('report', {
    keyPrefix: 'actual'
  }).t;
  var selectQueueDropdown = useMemo(function () {
    return createQueueDropdownSelector(isAllQueue, t);
  }, [isAllQueue, t]);
  return selectQueueDropdown(data);
};
export var useQueueLocationsAPI = function (queueId) {
  var _a = useAPI(QUEUE_LOCATIONS_API, {
      queueId: queueId && queueId !== ALL_QUEUES_ITEM.uniqueId ? queueId : undefined,
      selectionOnly: 'true'
    }),
    data = _a[0],
    fetchData = _a[1],
    clearData = _a[2];
  var fetchDataWrapper = useCallback(function () {
    if (queueId && queueId !== ALL_QUEUES_ITEM.uniqueId) fetchData();
  }, [fetchData, queueId]);
  return [data, fetchDataWrapper, clearData];
};
export var useScenarioDropdown = function () {
  var data = useReportAPI()[0].data;
  var selectScenariosDropdown = useMemo(function () {
    return createScenariosDropdownSelector();
  }, []);
  return selectScenariosDropdown(data);
};
export var selectMonthlyOpts = createSelector(selectPeriods, selectPlan, function (periods, plan) {
  if (!plan || plan.planType === PlanType.Weekly) return undefined;
  return periods;
});
export var getPlanDate = function (planType, value, suffix) {
  var valueMoment = moment.isMoment(value) ? value : moment.utc(value, translate('dateFormat.dayMonthYearFormat'), true);
  switch (planType) {
    case PlanType.MonthlyCustom:
      {
        var label = "".concat(suffix, " (").concat(valueMoment.format(translate('dateFormat.monthYearFormat')), ")");
        return {
          moment: valueMoment,
          name: label,
          table: label,
          chart: label
        };
      }
    case PlanType.Monthly:
      {
        var tableChartLabel = valueMoment.format(translate('dateFormat.monthYearFormat'));
        return {
          moment: valueMoment,
          name: valueMoment.format(translate('dateFormat.longMonthYearFormat')),
          table: tableChartLabel,
          chart: tableChartLabel
        };
      }
    case PlanType.Weekly:
      {
        var label = "".concat(suffix, " (").concat(value, ")");
        return {
          moment: valueMoment,
          name: label,
          table: label,
          chart: label
        };
      }
    default:
      return {
        moment: valueMoment,
        name: '',
        table: '',
        chart: ''
      };
  }
};
export var selectTimelineOptions = createSelector(selectMonthlyOpts, selectWeekDate, selectWeeks, selectPlan, function (monthly, weekly, weeks, plan) {
  var _a;
  return ((_a = monthly !== null && monthly !== void 0 ? monthly : weekly) !== null && _a !== void 0 ? _a : []).map(function (name, id) {
    return __assign({
      id: id
    }, getPlanDate(plan === null || plan === void 0 ? void 0 : plan.planType, name, weeks ? weeks[id] : ''));
  });
});
export var useReportTimelineOptions = function () {
  var data = useReportAPI()[0].data;
  return selectTimelineOptions(data);
};
var selectMetricSettingsData = function (data) {
  return data;
};
export var selectMetricSettingsMap = createSelector(selectMetricSettingsData, function (metricSettings) {
  return keyBy(metricSettings === null || metricSettings === void 0 ? void 0 : metricSettings.metrics, 'metric');
});
export var useMetricSettingsData = function () {
  var data = useMetricSettingsAPI()[0].data;
  return selectMetricSettingsMap(data);
};
export var useActualReportAPI = function () {
  return useAPI(ACTUAL_REPORT_API);
};
export var useReportSummaryData = function () {
  var _a = useActualReportAPI(),
    _b = _a[0].data,
    data = _b === void 0 ? {} : _b,
    clearActualReport = _a[2];
  useEffect(function () {
    return function () {
      clearActualReport();
    };
  }, [clearActualReport]);
  return useMemo(function () {
    var _a = data.details,
      details = _a === void 0 ? {} : _a,
      chart = data.chart;
    var detailMap = cloneDeep(details);
    var chartMap = cloneDeep(chart);
    // If abandonmentRate exists, set OFFERED to ACTUAL for both details and chart
    if (detailMap.abandonmentRate) {
      detailMap[PERIOD_DATA_FIELDS.OFFERED] = detailMap[PERIOD_DATA_FIELDS.ACTUAL];
    }
    if (chartMap === null || chartMap === void 0 ? void 0 : chartMap.abandonmentRate) {
      chartMap[PERIOD_DATA_FIELDS.OFFERED] = chartMap[PERIOD_DATA_FIELDS.ACTUAL];
    }
    return __assign(__assign({}, data), {
      details: detailMap,
      chart: chartMap || detailMap
    });
  }, [data]);
};
export var useManageSettings = function () {
  var settingData = useMemo(function () {
    return {};
  }, []);
  return settingData;
};
export var formattedDate = function (date) {
  var lang = getLanguage();
  moment.locale(lang);
  var m;
  if (moment.isMoment(date)) {
    m = date;
  } else if (date instanceof Date) {
    m = moment(date);
  } else {
    m = moment(date, i18next.t('translation:dateFormat.dayMonthYearFormat'), true);
    if (!m.isValid()) {
      m = moment(date, moment.ISO_8601, true);
    }
  }
  return m.isValid() ? m.format(i18next.t('translation:dateFormat.dayMonthYearFormat')) : '';
};
export var formatDayOfWeek = function (dateStr) {
  var lang = getLanguage();
  moment.locale(lang);
  var m;
  if (moment.isMoment(dateStr)) {
    m = dateStr;
  } else if (dateStr instanceof Date) {
    m = moment(dateStr);
  } else {
    m = moment(dateStr, i18next.t('translation:dateFormat.dayMonthYearFormat'), true);
    if (!m.isValid()) {
      m = moment(dateStr, moment.ISO_8601, true);
    }
  }
  return m.isValid() ? m.format('dddd') : '';
};
export var getSummaryBoxes = function (metric) {
  switch (metric) {
    case METRICS_ENUM.SERVICE_LEVEL:
      return __spreadArray(__spreadArray([], SUMMARY_BOX_BASE, true), [SUMMARY_BOX_THRESHOLD], false);
    case METRICS_ENUM.WORK_OFFERED_HANDLED:
      return __spreadArray(__spreadArray([], SUMMARY_BOX_BASE, true), SUMMARY_BOX_HANDLED, true);
    case METRICS_ENUM.ASA:
    case METRICS_ENUM.OCCUPANCY:
    case METRICS_ENUM.AHT:
    case METRICS_ENUM.CONCURRENCY:
    case METRICS_ENUM.PRODUCTION_HOURS:
    case METRICS_ENUM.WORKLOAD_HOURS:
    case METRICS_ENUM.REQUIRED_FTE:
    case METRICS_ENUM.PRODUCTIVE_FTE:
    case METRICS_ENUM.REQUIRED_HEADCOUNT:
    case METRICS_ENUM.ATTRITION:
    default:
      return SUMMARY_BOX_BASE;
  }
};
export var getOptionFormat = function (metric, actualAttritionMode) {
  switch (metric) {
    case METRICS_ENUM.SERVICE_LEVEL:
    case METRICS_ENUM.OCCUPANCY:
    case METRICS_ENUM.ABANDONMENT_RATE:
      return {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      };
    case METRICS_ENUM.ATTRITION:
      return actualAttritionMode === ACTUAL_ATTRITION_MODE.ABSOLUTE ? {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      } : {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      };
    case METRICS_ENUM.ASA:
    case METRICS_ENUM.AHT:
      return {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      };
    case METRICS_ENUM.CONCURRENCY:
    case METRICS_ENUM.REQUIRED_FTE:
    case METRICS_ENUM.PRODUCTIVE_FTE:
      return {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      };
    case METRICS_ENUM.WORK_OFFERED_HANDLED:
    case METRICS_ENUM.PRODUCTION_HOURS:
    case METRICS_ENUM.WORKLOAD_HOURS:
    case METRICS_ENUM.REQUIRED_HEADCOUNT:
      return {
        maximumFractionDigits: 0
      };
    default:
      // VARIANCE
      return {
        style: 'percent',
        signDisplay: 'exceptZero',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      };
  }
};
export var useVarianceColor = function (type) {
  var metricRanges = useMetricSettingsData();
  return useCallback(function (value) {
    var _a;
    var range = (_a = metricRanges[type]) === null || _a === void 0 ? void 0 : _a.metadata;
    if (!range || !isNumber(value)) return '';
    var acceptable = range.acceptable,
      caution = range.caution;
    var newValue = value * 100; // Convert to decimal for percentage comparison
    if (newValue >= acceptable.min && newValue <= acceptable.max) {
      return COLOR_DEFAULT_VARIANCE[RANGE_COLORS.ACCEPTABLE];
    }
    if (newValue >= caution.min && newValue <= caution.max) {
      return COLOR_DEFAULT_VARIANCE[RANGE_COLORS.CAUTION];
    }
    return COLOR_DEFAULT_VARIANCE[RANGE_COLORS.OUTSIDE];
  }, [metricRanges, type]);
};
export var getTickRange = function (data, nTicks, metric, actualAttritionMode) {
  if (nTicks === void 0) {
    nTicks = 7;
  }
  // Default range (0–100) or metric-specific
  var _a = metric ? {
      min: MAXIMUM_VALUES[metric].min,
      max: Math.ceil(MAXIMUM_VALUES[metric].max / 10) * 10
    } : {
      min: 0,
      max: 1
    },
    min = _a.min,
    max = _a.max;
  // Handle percentage metrics (normalize to 0–1 scale)
  var isMetricPercentage = __spreadArray([METRICS_ENUM.SERVICE_LEVEL, METRICS_ENUM.OCCUPANCY, METRICS_ENUM.ABANDONMENT_RATE], actualAttritionMode === ACTUAL_ATTRITION_MODE.ABSOLUTE ? [] : [METRICS_ENUM.ATTRITION], true).includes(metric);
  if (!metric || metric && isMetricPercentage) {
    max = max / 100;
  }
  // Early return if no valid data
  if (!(data === null || data === void 0 ? void 0 : data.length) || data.every(function (val) {
    return val == null || val === 0;
  })) {
    var rangeMin_1 = Math.min(0, min);
    var rangeMax_1 = Math.max(1, max);
    return {
      range: [rangeMin_1, rangeMax_1],
      dtick: (rangeMax_1 - rangeMin_1) / nTicks
    };
  }
  var minVal = Math.min.apply(Math, data);
  var maxVal = Math.max.apply(Math, data);
  var dataRange = maxVal - minVal || 1;
  // Add 10% padding to range
  var paddedRange = dataRange + Math.abs(dataRange) * 0.1;
  var rawStep = paddedRange / nTicks;
  // Choose step size based on "nice" numbers
  var pow10 = Math.pow(10, Math.floor(Math.log10(rawStep)));
  var stepCandidates = [0.1, 0.2, 0.5, 1, 2, 5, 10];
  var step = stepCandidates[0] * pow10;
  for (var _i = 0, stepCandidates_1 = stepCandidates; _i < stepCandidates_1.length; _i++) {
    var candidate = stepCandidates_1[_i];
    if (candidate * pow10 >= rawStep) {
      step = candidate * pow10;
      break;
    }
  }
  if (metric && !isMetricPercentage && step < 1) step = 1;
  // Expand range to nearest step
  var lowerBound = Math.floor(minVal / step) * step;
  var upperBound = Math.ceil(maxVal / step) * step;
  if (lowerBound >= minVal) lowerBound -= step;
  if (upperBound <= maxVal) upperBound += step;
  // Apply metric constraints if defined
  var rangeMin = lowerBound;
  if (metric) {
    if (lowerBound < 1) {
      rangeMin = 0;
    } else {
      rangeMin = Math.max(lowerBound, min);
    }
  }
  var rangeMax = metric && !isMetricPercentage ? Math.ceil(upperBound) : upperBound;
  return {
    range: [rangeMin, rangeMax],
    dtick: step
  };
};
export var getTemplateTooltip = function (name, values, isVariance) {
  return {
    hoverlabel: {
      bordercolor: '#fff'
    },
    hovertemplate: "<b style='color:#fff'>".concat(name, "</b>") + "<span style='color:#fff'>: ".concat(isVariance ? '%{customdata}' : '%{y}', " </span><extra></extra>"),
    customdata: values.map(function (v) {
      return formatNumberByLocale(v, getOptionFormat());
    })
  };
};
export var getAxisFormatTicks = function (metric, actualAttritionMode) {
  switch (metric) {
    case METRICS_ENUM.SERVICE_LEVEL:
    case METRICS_ENUM.OCCUPANCY:
    case METRICS_ENUM.ABANDONMENT_RATE:
      // Output: 1.0%
      return '.1%';
    case METRICS_ENUM.ATTRITION:
      return actualAttritionMode === ACTUAL_ATTRITION_MODE.ABSOLUTE ? ',.1f' : '.1%';
    case METRICS_ENUM.CONCURRENCY:
    case METRICS_ENUM.REQUIRED_FTE:
    case METRICS_ENUM.PRODUCTIVE_FTE:
      // Output: 1.0
      return ',.1f';
    case METRICS_ENUM.ASA:
    case METRICS_ENUM.AHT:
    case METRICS_ENUM.WORK_OFFERED_HANDLED:
    case METRICS_ENUM.PRODUCTION_HOURS:
    case METRICS_ENUM.WORKLOAD_HOURS:
    default:
      // Output: 1,000
      return ',.0f';
  }
};
var decimalsFor = function (step) {
  return step >= 1 ? 0 : Math.max(0, -Math.floor(Math.log10(step)));
};
var roundTo = function (n, d) {
  var f = Math.pow(10, d);
  return Math.round(n * f) / f;
};
var canUseCandidate = function (range, step, requiredSpan) {
  return range <= step * requiredSpan;
};
var findStartingIndex = function (range, stepCandidates, requiredSpan, maxVal, minDTickForHighRanges) {
  var start = stepCandidates.findIndex(function (s) {
    return canUseCandidate(range, s, requiredSpan);
  });
  if (start === -1) start = stepCandidates.length - 1;
  if (maxVal >= 0.05) {
    var minIdx = stepCandidates.findIndex(function (s) {
      return s >= minDTickForHighRanges;
    });
    if (minIdx !== -1) start = Math.max(start, minIdx);
  }
  return start;
};
export function computeFlat(v, isPercentageMetric, flatPadding, requiredSpan) {
  var _a, _b;
  var absV = Math.abs(v);
  var dTick = absV < 0.01 ? 0.001 : 0.01;
  if (absV >= 1) dTick = Math.max(dTick, 0.01);
  var d = decimalsFor(dTick);
  var left = (_a = flatPadding.leftSteps) !== null && _a !== void 0 ? _a : 3;
  var right = (_b = flatPadding.rightSteps) !== null && _b !== void 0 ? _b : 5;
  var totalSpan = dTick * requiredSpan;
  var makeResult = function (minAxis, maxAxis) {
    return {
      autorange: true,
      autorangeoptions: {
        minallowed: isPercentageMetric && roundTo(minAxis, d) === 0 ? 'auto' : roundTo(minAxis, d),
        maxallowed: roundTo(maxAxis, d)
      },
      dTick: dTick
    };
  };
  if (isPercentageMetric) {
    var minAxis_1 = v - left * dTick;
    var maxAxis_1 = v + right * dTick;
    if (minAxis_1 < 0) {
      minAxis_1 = 0;
      maxAxis_1 = Math.max(v, minAxis_1 + totalSpan);
    }
    return makeResult(minAxis_1, maxAxis_1);
  }
  var minAxis = v - totalSpan / 2;
  var maxAxis = minAxis + totalSpan;
  return makeResult(minAxis, maxAxis);
}
function centerRange(minVal, maxVal, targetSpan, dTick) {
  var d = decimalsFor(dTick);
  var midpoint = (minVal + maxVal) / 2;
  var minAxis = roundTo(midpoint - targetSpan / 2, d);
  var maxAxis = roundTo(midpoint + targetSpan / 2, d);
  return {
    minAxis: minAxis,
    maxAxis: maxAxis,
    dTick: dTick,
    d: d
  };
}
export function adjustToCoverData(minAxis, maxAxis, minVal, maxVal, targetSpan, dTick) {
  if (minAxis > minVal) {
    minAxis -= Math.ceil((minAxis - minVal) / dTick) * dTick;
    maxAxis = minAxis + targetSpan;
  }
  if (maxAxis < maxVal) {
    maxAxis += Math.ceil((maxVal - maxAxis) / dTick) * dTick;
    minAxis = maxAxis - targetSpan;
  }
  return {
    minAxis: minAxis,
    maxAxis: maxAxis
  };
}
function clampVarianceNegativeRoom(minAxis, maxVal, dTick, maxNegativeStepsForVariance) {
  var negLimit = -dTick * maxNegativeStepsForVariance;
  return minAxis > maxVal ? Math.min(minAxis, negLimit) : minAxis;
}
export function ensureSafePadding(minAxis, maxAxis, dTick) {
  var range = maxAxis - minAxis;
  if (range === 0) {
    var pad = dTick * 2;
    return {
      minAxis: minAxis - pad,
      maxAxis: maxAxis + pad
    };
  }
  var extra = dTick * 1;
  return {
    minAxis: minAxis - extra,
    maxAxis: maxAxis + extra
  };
}
function computeVariable(minVal, maxVal, dTick, requiredSpan, isPercentageMetric, maxNegativeStepsForVariance) {
  var range = maxVal - minVal;
  var targetSpan = Math.max(range, dTick * requiredSpan);
  var base = centerRange(minVal, maxVal, targetSpan, dTick);
  if (isPercentageMetric) {
    var zeroSafe = Math.max(0, base.minAxis);
    var maxAxis_2 = Math.max(base.maxAxis, zeroSafe + targetSpan);
    var safe_1 = ensureSafePadding(zeroSafe, maxAxis_2, dTick);
    return __assign(__assign({}, safe_1), {
      dTick: base.dTick,
      d: base.d
    });
  }
  var _a = adjustToCoverData(base.minAxis, base.maxAxis, minVal, maxVal, targetSpan, dTick),
    minAxis = _a.minAxis,
    maxAxis = _a.maxAxis;
  minAxis = clampVarianceNegativeRoom(minAxis, maxVal, dTick, maxNegativeStepsForVariance);
  var safe = ensureSafePadding(minAxis, maxAxis, dTick);
  return __assign(__assign({}, safe), {
    dTick: base.dTick,
    d: base.d
  });
}
export function computePercentageRange(values, type) {
  if (type === void 0) {
    type = CHART_KEYS.VARIANCE;
  }
  var _a = RANGE_OPTIONS.nTicks,
    nTicks = _a === void 0 ? 9 : _a,
    stepCandidates = RANGE_OPTIONS.stepCandidates,
    flatPadding = RANGE_OPTIONS.flatPadding,
    maxNegativeStepsForVariance = RANGE_OPTIONS.maxNegativeStepsForVariance,
    minDTickForHighRanges = RANGE_OPTIONS.minDTickForHighRanges;
  if (!(values === null || values === void 0 ? void 0 : values.some(function (v) {
    return isNumber(v);
  }))) return {
    range: [0, 1]
  };
  var isPercentageMetric = type === METRICS_ENUM.ABANDONMENT_RATE;
  var minVal = _min(values);
  var maxVal = _max(values);
  var range = maxVal - minVal;
  var requiredSpan = nTicks - 1;
  if (range === 0) return computeFlat(minVal, isPercentageMetric, flatPadding, requiredSpan);
  var idx = findStartingIndex(range, stepCandidates, requiredSpan, maxVal, minDTickForHighRanges);
  var dTick = stepCandidates[idx];
  var result = computeVariable(minVal, maxVal, dTick, requiredSpan, isPercentageMetric, maxNegativeStepsForVariance);
  var minRounded = roundTo(result.minAxis, result.d);
  return {
    autorange: true,
    autorangeoptions: {
      minallowed: isPercentageMetric && minRounded === 0 ? 'auto' : minRounded,
      maxallowed: roundTo(result.maxAxis, result.d)
    },
    dTick: result.dTick
  };
}
export var getSummaryChart = function (title, controls) {
  var _a, _b, _c;
  var layout = cloneDeep(lineBarLayout);
  var metricTab = controls.metricTab,
    frequency = controls.frequency,
    dailyViewType = controls.dailyViewType,
    sampleData = controls.sampleData,
    labels = controls.labels,
    chart = controls.chart,
    actualAttritionMode = controls.actualAttritionMode;
  var _d = CHART_HEIGHT_CONFIG[frequency],
    baseHeight = _d.baseHeight,
    overflowHeight = _d.overflowHeight,
    rangeDefault = _d.rangeDefault,
    barGapDefault = _d.barGapDefault,
    marginBottomDefault = _d.marginBottomDefault;
  var _e = DAILY_VIEW_RANGE_CONFIG[dailyViewType],
    range = _e.range,
    barGap = _e.barGap,
    marginBottom = _e.marginBottom,
    innerHeight = _e.innerHeight,
    overflowInnerHeight = _e.overflowInnerHeight;
  var _f = rangeDefault || range,
    min = _f[0],
    max = _f[1];
  var hasOverflow = (labels === null || labels === void 0 ? void 0 : labels.length) + min > max;
  // Determine the appropriate height based on overflow status
  var innerHeightLayout = hasOverflow ? overflowHeight || overflowInnerHeight : baseHeight || innerHeight;
  var bottomMarginLayout = marginBottomDefault || marginBottom;
  var keyPrefix = 'report:actual.summaryChart';
  var data = sampleData.map(function (item) {
    var _a;
    var name = i18next.t("".concat(keyPrefix, ".legend.").concat(item.key));
    var values = (_a = chart[item.key]) !== null && _a !== void 0 ? _a : [];
    return __assign(__assign(__assign(__assign({}, item), {
      name: name,
      y: values
    }), (labels === null || labels === void 0 ? void 0 : labels.length) === 1 && item.type === 'scatter' && {
      visible: 'legendonly'
    }), getTemplateTooltip(name, values, item.key === CHART_KEYS.VARIANCE));
  });
  set(layout, 'bargap', barGapDefault || barGap);
  set(layout, 'height', innerHeightLayout);
  if (!hasOverflow) {
    set(layout, 'margin.b', bottomMarginLayout); // If no overflow is default margin bottom
  }
  var isAbandonmentRate = ((_a = sampleData[2]) === null || _a === void 0 ? void 0 : _a.key) === CHART_KEYS.ABANDONMENT_RATE;
  var dataY = __spreadArray(__spreadArray([], chart[(_b = sampleData[0]) === null || _b === void 0 ? void 0 : _b.key] || [], true), chart[(_c = sampleData[1]) === null || _c === void 0 ? void 0 : _c.key] || [], true);
  var _g = getTickRange(dataY, 8, metricTab, actualAttritionMode),
    yAxisRange = _g.range,
    yAxisDTick = _g.dtick;
  set(layout, 'yaxis', __assign(__assign({}, layout.yaxis), {
    title: {
      text: i18next.t("".concat(keyPrefix, ".").concat(isAbandonmentRate ? 'workVolume' : metricTab)),
      standoff: 6,
      font: {
        size: 14,
        color: '#AAAAAA'
      }
    },
    range: yAxisRange,
    dtick: yAxisDTick,
    tickformat: getAxisFormatTicks(metricTab, actualAttritionMode)
  }));
  // Set titles and ranges for yAxis2 (type line) only when an auxiliary third series exists
  if (sampleData[2]) {
    var rangeOptions = computePercentageRange(chart[sampleData[2].key], isAbandonmentRate ? METRICS_ENUM.ABANDONMENT_RATE : CHART_KEYS.VARIANCE);
    set(layout, 'yaxis2', __assign(__assign(__assign({}, layout.yaxis2), {
      title: {
        text: i18next.t("".concat(keyPrefix, ".legend.").concat(sampleData[2].key)),
        standoff: 6,
        font: {
          size: 14,
          color: '#e88b1e'
        }
      }
    }), rangeOptions));
  } else {
    delete layout.yaxis2;
  }
  // Set range for xAxis
  set(layout, 'xaxis', __assign(__assign({}, layout.xaxis), {
    ticktext: labels,
    tickvals: rangeFromLodash(labels === null || labels === void 0 ? void 0 : labels.length),
    range: [min, Math.min(max, (labels === null || labels === void 0 ? void 0 : labels.length) + min)],
    rangeslider: {
      visible: hasOverflow,
      range: [min, (labels === null || labels === void 0 ? void 0 : labels.length) + min]
    }
  }));
  var xAxisConfig = {
    rangeFixed: [min, max],
    maxRange: (labels === null || labels === void 0 ? void 0 : labels.length) - 1 || 0,
    viewRange: max - min
  };
  return {
    title: title,
    layout: layout,
    data: data,
    xAxisConfig: xAxisConfig
  };
};
export var useUpdateActualDataManual = function () {
  return useAPI(ACTUAL_REPORT_API, {}, 'PUT');
};
var createScenarioSelectorById = function (planId) {
  return function (data) {
    var _a;
    var scenarioFinded = (_a = data === null || data === void 0 ? void 0 : data.scenarios) === null || _a === void 0 ? void 0 : _a.find(function (scenario) {
      return scenario.id === planId;
    });
    if (!scenarioFinded) return null;
    return __assign(__assign({}, scenarioFinded), {
      name: scenarioFinded.isBaseline ? i18next.t('translation:baseline') : scenarioFinded.name
    });
  };
};
export var useSelectScenarioById = function (planId) {
  var data = useReportAPI()[0].data;
  var selectScenario = useMemo(function () {
    return createScenarioSelectorById(planId);
  }, [planId]);
  return selectScenario(data);
};
export var usePlanColorById = function (planId) {
  var _a;
  var scenario = useSelectScenarioById(planId);
  return PLAN_COLORS[(_a = scenario === null || scenario === void 0 ? void 0 : scenario.colorIndex) !== null && _a !== void 0 ? _a : 0];
};
export var createParamsFetchActual = function (data) {
  var planId = data.planId,
    queueId = data.queueId,
    queueLocationId = data.queueLocationId,
    frequency = data.frequency,
    metricTab = data.metricTab,
    periodStart = data.periodStart,
    periodEnd = data.periodEnd,
    dailyViewType = data.dailyViewType,
    specificDay = data.specificDay,
    actualAttritionMode = data.actualAttritionMode;
  // Use index signature to allow dynamic keys
  var params = {
    planId: planId,
    queueId: queueId,
    viewType: frequency,
    metricType: metricTab,
    periodStart: periodStart,
    periodEnd: periodEnd,
    actualAttritionMode: actualAttritionMode
  };
  if (queueLocationId !== undefined && queueLocationId !== ALL_LOCATIONS_ID) {
    params.queueLocationId = queueLocationId;
  }
  if (frequency === FREQUENCY_ENUM.DAILY) {
    params.filterType = dailyViewType;
    if (dailyViewType === DAILY_FILTER_KEYS.SPECIFIC_DAY && specificDay) {
      params.filterDate = moment(specificDay).format(DATE_PICKER_SLASH_FORMAT);
    }
  }
  return params;
};
export var useActualStatus = function () {
  return useAPI(GET_ACTUAL_QUEUE_STATUS);
};
export var useActualStatusData = function () {
  var data = useActualStatus()[0].data;
  return data;
};
// Selector to get daily disabled status (true if disabled)
export var useSelectDisabledDaily = function () {
  var data = useActualStatusData();
  return useMemo(function () {
    return data === null || data === void 0 ? void 0 : data.disabledDaily;
  }, [data]);
};
// Selector to get a map of queueId => isDisabled (true if disabled)
export var useSelectDisabledQueues = function () {
  var data = useActualStatusData();
  return useMemo(function () {
    var queues = data === null || data === void 0 ? void 0 : data.queues;
    if (Array.isArray(queues)) {
      return mapValues(keyBy(queues, 'id'), 'disabled');
    }
    return {};
  }, [data]);
};
// Selector to get a map of metricName => isDisabled (true if disabled)
export var useSelectDisabledMetrics = function () {
  var data = useActualStatusData();
  return useMemo(function () {
    var metrics = data === null || data === void 0 ? void 0 : data.metrics;
    if (Array.isArray(metrics)) {
      return mapValues(keyBy(metrics, 'name'), 'disabled');
    }
    return {};
  }, [data]);
};
export var useSelectFirstEnabledMetric = function (sectionType) {
  var metricDisabled = useSelectDisabledMetrics();
  return useMemo(function () {
    var _a, _b;
    var found = (_a = METRIC_LIST[sectionType]) === null || _a === void 0 ? void 0 : _a.find(function (metric) {
      return !metricDisabled[metric];
    });
    return found !== null && found !== void 0 ? found : (_b = METRIC_LIST[sectionType]) === null || _b === void 0 ? void 0 : _b[0];
  }, [metricDisabled, sectionType]);
};
export var useSelectIntervalTime = function (frequency, dailyViewType, queueId) {
  var planData = useReportAPI()[0].data;
  var planType = selectPlanType(planData);
  var queue = useQueueById(planData === null || planData === void 0 ? void 0 : planData.id, queueId);
  return useMemo(function () {
    if (frequency === FREQUENCY_ENUM.PERIOD) {
      return [PlanType.Monthly, PlanType.MonthlyCustom].includes(planType) ? INTERVAL_TYPE.MONTHLY : INTERVAL_TYPE.WEEKLY;
    }
    if (dailyViewType === DAILY_FILTER_KEYS.DAILY_AVERAGE) {
      return INTERVAL_TYPE.DAILY;
    }
    return (queue === null || queue === void 0 ? void 0 : queue.timeInterval) && queue.timeInterval in DAILY_INTERVAL ? DAILY_INTERVAL[queue.timeInterval] : INTERVAL_TYPE.FIFTEEN_MINUTES;
  }, [frequency, dailyViewType, planType, queue === null || queue === void 0 ? void 0 : queue.timeInterval]);
};
export var getFormatterValueByMetric = function (value, metricTab, actualAttritionMode) {
  var isPercentage = isNumber(value) && (metricTab === METRICS_ENUM.ATTRITION ? actualAttritionMode === ACTUAL_ATTRITION_MODE.PERCENTAGE : metricTab in PERCENTAGES);
  if (isPercentage) {
    return value * 100;
  }
  return value;
};
export var useDeleteManualInputDataAPI = function () {
  return useAPI(ACTUAL_REPORT_API, undefined, 'DELETE');
};
export var useAsyncActualExport = function (url) {
  if (url === void 0) {
    url = EXPORT_API;
  }
  var _a = useAPI(url, undefined, 'POST'),
    downloadData = _a[0],
    fetchDownloadData = _a[1],
    clearDownloadData = _a[2];
  var data = downloadData.data,
    error = downloadData.error,
    _b = downloadData.requesting,
    requesting = _b === void 0 ? false : _b;
  useEffect(function () {
    return clearDownloadData;
  }, [clearDownloadData]);
  return [{
    data: data,
    error: error,
    requesting: requesting
  }, fetchDownloadData, clearDownloadData];
};
export var useTimelineSync = function (_a) {
  var timelines = _a.timelines,
    periodStart = _a.periodStart,
    periodEnd = _a.periodEnd,
    isLimitedTimeline = _a.isLimitedTimeline,
    isShowEndTimeLine = _a.isShowEndTimeLine,
    setValue = _a.setValue;
  var handleTimelineChange = useCallback(function (field, value) {
    var maxIndex = timelines ? timelines.length - 1 : 0;
    var clampIndex = function (idx) {
      return Math.max(0, Math.min(maxIndex, idx));
    };
    var updateEndFromStart = function (startIndex) {
      var isLimited = periodEnd - startIndex >= LIMIT_TIMELINE;
      var limitSize = isLimitedTimeline && isLimited ? LIMIT_TIMELINE - 1 : 0;
      var newEnd = clampIndex(startIndex + limitSize);
      var needsUpdate = startIndex > periodEnd || isLimitedTimeline && isLimited && startIndex < periodEnd && periodEnd > startIndex + limitSize;
      if (needsUpdate) {
        setValue(PERIOD_KEY.END, newEnd);
      }
    };
    var updateStartFromEnd = function (endIndex) {
      var isLimited = endIndex - periodStart >= LIMIT_TIMELINE;
      var limitSize = isLimitedTimeline && isLimited ? LIMIT_TIMELINE - 1 : 0;
      var newStart = clampIndex(endIndex - limitSize);
      var needsUpdate = endIndex < periodStart || isLimitedTimeline && isLimited && endIndex > periodStart + limitSize;
      if (needsUpdate) {
        setValue(PERIOD_KEY.START, newStart);
      }
    };
    if (field === PERIOD_KEY.START && isNumber(value)) {
      setValue(PERIOD_KEY.START, value);
    }
    if (field === PERIOD_KEY.END && isNumber(value)) {
      setValue(PERIOD_KEY.END, value);
    }
    if (field === PERIOD_KEY.START || isLimitedTimeline) {
      updateEndFromStart(isNumber(value) ? value : periodStart);
    }
    if (field === PERIOD_KEY.END) {
      updateStartFromEnd(isNumber(value) ? value : periodEnd);
    }
  }, [periodStart, periodEnd, timelines, isLimitedTimeline, setValue]);
  useEffect(function () {
    if (isLimitedTimeline) {
      handleTimelineChange();
    }
  }, [isLimitedTimeline, handleTimelineChange]);
  useEffect(function () {
    if (isShowEndTimeLine) {
      handleTimelineChange();
    } else {
      setValue(PERIOD_KEY.END, periodStart);
    }
  }, [isShowEndTimeLine, periodStart, setValue, handleTimelineChange]);
  return handleTimelineChange;
};